import { useEffect, useMemo, useRef, useState } from "react";
import { Button, Input, Select, Image, Spin, message } from "antd";
import { Editor } from "@tinymce/tinymce-react";
import { Search, X, Eye } from "lucide-react";
import dayjs from "dayjs";
import CommentItem from "./CommentItem";

// --- CÁC INTERFACE MÔ PHỎNG (Do đã bỏ import từ API) ---
export interface WorkLogItem {
    id: number;
    content: string;
    created_at: string;
    created_by_id: number;
    creator: {
        name?: string;
        fullname?: string;
    };
}

interface NotePhoto {
    uid: string;
    id?: number;
    url: string;
    uploading?: boolean;
}

const PAGE_SIZE = 20;
const MAX_PHOTO_SIZE = 10 * 1024 * 1024; // 10MB/ảnh

// Hàm tạo dữ liệu ảo để test
const generateMockLogs = (page: number, size: number): WorkLogItem[] => {
    return Array.from({ length: size }).map((_, i) => {
        const id = page * 100 + i;
        const isUserA = id % 2 === 0;
        return {
            id,
            content: `<p>Đây là nội dung bình luận mẫu số ${id} để demo giao diện.</p>`,
            created_at: new Date(Date.now() - id * 10000000).toISOString(),
            created_by_id: isUserA ? 1 : 2,
            creator: { name: isUserA ? "Nguyễn Văn A" : "Trần Thị B" },
        };
    });
};

export default function CommentSection({ customerId }: { customerId?: number }) {
    const [content, setContent] = useState("");
    const [sending, setSending] = useState(false);
    const [search, setSearch] = useState("");
    const [employee, setEmployee] = useState<number>();

    // Lịch sử công việc
    const [logs, setLogs] = useState<WorkLogItem[]>([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [loading, setLoading] = useState(false);

    // Ảnh dán từ clipboard
    const [notePhotos, setNotePhotos] = useState<NotePhoto[]>([]);
    const customerIdRef = useRef<number | undefined>(customerId);
    customerIdRef.current = customerId;
    const newUid = () => `${Date.now()}_${Math.random().toString(36).slice(2)}`;

    // ================== MÔ PHỎNG API FETCH ==================
    const fetchLogs = (p = 1, append = false) => {
        setLoading(true);
        // Giả lập delay mạng 500ms
        setTimeout(() => {
            const mockData = generateMockLogs(p, p >= 3 ? 5 : PAGE_SIZE); // Giả sử chỉ có ~45 bản ghi
            setLogs((prev) => (append ? [...prev, ...mockData] : mockData));
            setTotal(45); // Tổng số bản ghi giả định
            setLoading(false);
        }, 500);
    };

    useEffect(() => {
        setLogs([]);
        setTotal(0);
        setPage(1);
        fetchLogs(1);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [customerId]);

    useEffect(() => {
        setNotePhotos((prev) => {
            prev.forEach((p) => { if (!p.id) URL.revokeObjectURL(p.url); });
            return [];
        });
    }, [customerId]);

    // ================== MÔ PHỎNG API UPLOAD ẢNH ==================
    const handlePastedFiles = (files: File[]) => {
        files.forEach((file) => {
            const uid = newUid();
            const url = URL.createObjectURL(file);

            // Thêm ảnh vào state với trạng thái uploading
            setNotePhotos((prev) => [...prev, { uid, url, uploading: true }]);

            // Giả lập thời gian upload ảnh mất 1 giây
            setTimeout(() => {
                const isSuccess = Math.random() > 0.1; // 90% tỷ lệ upload thành công
                if (isSuccess) {
                    setNotePhotos((prev) =>
                        prev.map((p) => (p.uid === uid ? { ...p, id: Date.now(), uploading: false } : p))
                    );
                } else {
                    setNotePhotos((prev) => prev.filter((p) => p.uid !== uid));
                    message.error("Tải ảnh lên thất bại (Demo lỗi mạng)");
                }
            }, 1000);
        });
    };

    // ================== MÔ PHỎNG XÓA ẢNH ==================
    const removeNotePhoto = async (uid: string) => {
        const p = notePhotos.find((x) => x.uid === uid);
        if (!p || p.uploading) return;

        // Không gọi API, chỉ xóa luôn khỏi state
        setNotePhotos((prev) => prev.filter((x) => x.uid !== uid));
    };

    // ================== MÔ PHỎNG GỬI BÌNH LUẬN ==================
    const handleSend = async () => {
        // 1. Lọc bỏ các thẻ HTML rỗng để kiểm tra xem có nội dung thật không
        const plainText = content.replace(/<[^>]*>/g, "").replace(/&nbsp;/g, " ").trim();

        if (!plainText && notePhotos.length === 0) {
            message.warning("Vui lòng nhập nội dung");
            return;
        }

        if (notePhotos.some((p) => p.uploading)) {
            message.warning("Vui lòng chờ ảnh tải lên xong");
            return;
        }

        // 2. Chèn ảnh dán (nếu có) vào nội dung HTML
        const imagesHtml = notePhotos.length
            ? `<p>${notePhotos
                .map((p) => `<img src="${p.url}" style="max-width:140px;border-radius:8px;margin:4px 4px 0 0" />`)
                .join("")}</p>`
            : "";

        setSending(true);

        // 3. Giả lập delay mạng (nếu bạn dùng API thật thì await call API ở đây)
        setTimeout(() => {
            // TẠO OBJECT DỮ LIỆU MỚI KHỚP VỚI YÊU CẦU CỦA COMMENT ITEM
            const newLog = {
                id: Date.now(), // Tạo ID tạm thời
                content: `${content}${imagesHtml}`, // Lấy nội dung từ Editor
                created_at: new Date().toISOString(), // Lấy thời gian hiện tại
                created_by_id: 1,
                creator: {
                    name: "Admin User", // Hiển thị tên Admin User như trong hình
                    fullname: "Admin User"
                }
            };

            // 4. Cập nhật state logs: Chèn dữ liệu mới lên đầu mảng
            setLogs((prev) => [newLog, ...prev]);
            setTotal((prev) => prev + 1);

            // 5. Reset form sau khi gửi thành công
            message.success("Đã gửi bình luận");
            setContent(""); // Xóa trắng Editor
            setNotePhotos([]); // Xóa hàng chờ ảnh
            setSending(false); // Tắt loading
        }, 500);
    };

    // Bộ lọc nhân viên
    const employeeOptions = useMemo(() => {
        const map = new Map<number, string>();
        logs.forEach((l) => {
            const name = l.creator?.name || l.creator?.fullname;
            if (l.created_by_id && name && !map.has(l.created_by_id)) map.set(l.created_by_id, name);
        });
        return Array.from(map.entries()).map(([value, label]) => ({ value, label }));
    }, [logs]);

    // Danh sách hiển thị dựa trên search và filter
    const comments = useMemo(() => {
        const kw = search.toLowerCase();
        return logs.filter((x) => {
            const name = (x.creator?.name || x.creator?.fullname || "").toLowerCase();
            const text = (x.content || "").replace(/<[^>]*>/g, " ").toLowerCase();
            const matchSearch = !kw || text.includes(kw) || name.includes(kw);
            const matchEmployee = employee == null || x.created_by_id === employee;
            return matchSearch && matchEmployee;
        });
    }, [logs, search, employee]);

    return (
        <div className="overflow-hidden">
            {/* ================= Editor ================= */}
            <Editor
                tinymceScriptSrc={`${process.env.PUBLIC_URL || ""}/tinymce/tinymce.min.js`}
                licenseKey="gpl"
                value={content}
                onEditorChange={setContent}
                init={{
                    height: 180,
                    menubar: false,
                    branding: false,
                    resize: false,
                    skin: document.documentElement.classList.contains("dark") ? "oxide-dark" : "oxide",
                    content_css: document.documentElement.classList.contains("dark") ? "dark" : "default",
                    plugins: ["link", "lists", "image", "table", "code", "fullscreen", "preview"],
                    toolbar: "undo redo | bold italic underline strikethrough | fontfamily fontsize | forecolor backcolor | alignleft aligncenter alignright | bullist numlist outdent indent | table image link | preview fullscreen code",
                    placeholder: "Nhập nội dung hoặc gõ # để thêm hashtag",
                    paste_data_images: false,
                    setup: (editor: any) => {
                        editor.on("paste", (e: ClipboardEvent) => {
                            const items = e.clipboardData?.items;
                            if (!items) return;
                            const files: File[] = [];
                            let tooLarge = false;
                            Array.from(items).forEach((item) => {
                                if (item.type.startsWith("image/")) {
                                    const file = item.getAsFile();
                                    if (!file) return;
                                    if (file.size > MAX_PHOTO_SIZE) { tooLarge = true; return; }
                                    files.push(file);
                                }
                            });
                            if (tooLarge) message.error("Ảnh vượt quá 10MB");
                            if (!files.length) return;
                            e.preventDefault();
                            handlePastedFiles(files);
                        });
                    },
                }}
            />

            {/* Khung chứa ảnh vừa dán */}
            {notePhotos.length > 0 && (
                <div className="flex flex-wrap gap-2 py-3 text-[#000000e0]">
                    {notePhotos.map((photo) => (
                        <div
                            key={photo.uid}
                            className="relative rounded-lg overflow-hidden border dark:border-[#2a2a2a] !w-[64px] !h-[64px]"
                        >
                            <Image
                                src={photo.url}
                                className="!w-[64px] !h-[64px] object-contain flex items-center justify-center"
                                preview={{ mask: <Eye size={18} /> }}
                            />
                            {photo.uploading && (
                                <div className="absolute inset-0 bg-white/60 flex items-center justify-center">
                                    <Spin size="small" />
                                </div>
                            )}
                            <button
                                className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-red-500 text-white flex justify-center items-center"
                                onClick={() => removeNotePhoto(photo.uid)}
                            >
                                <X size={14} />
                            </button>
                        </div>
                    ))}
                </div>
            )}

            {/* ================= Toolbar ================= */}
            <div className="flex items-center justify-end py-3">
                <Button
                    type="primary"
                    className="px-8"
                    loading={sending}
                    onClick={handleSend}
                >
                    Gửi
                </Button>
            </div>

            {/* ================= Filter ================= */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 py-3 pt-0">
                <Input
                    allowClear
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Tìm kiếm bình luận"
                    prefix={<Search size={16} />}
                />
                <Select
                    allowClear
                    value={employee}
                    onChange={setEmployee}
                    placeholder="Chọn nhân viên"
                    showSearch
                    optionFilterProp="label"
                    options={employeeOptions}
                />
            </div>

            {/* ================= List ================= */}
            <Spin spinning={loading}>
                <div className="max-h-[420px] overflow-y-auto">
                    {comments.map((item) => (
                        <CommentItem
                            key={item.id}
                            item={{
                                id: item.id,
                                avatar: "",
                                name: item.creator?.name || item.creator?.fullname || "—",
                                time: item.created_at ? dayjs(item.created_at).format("HH:mm - DD/MM/YYYY") : "",
                                content: item.content,
                            }}
                        />
                    ))}

                    {!loading && comments.length === 0 && (
                        <div className="py-10 text-center text-slate-400 font-medium">
                            Chưa có lịch sử công việc
                        </div>
                    )}

                    {logs.length < total && (
                        <div className="py-3 text-center">
                            <Button
                                onClick={() => {
                                    const next = page + 1;
                                    setPage(next);
                                    fetchLogs(next, true);
                                }}
                            >
                                Xem thêm ({logs.length}/{total})
                            </Button>
                        </div>
                    )}
                </div>
            </Spin>
        </div>
    );
}